import * as React from 'react'
import { Plus, Trash2, GripVertical, Star, Clock, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell, TableActionsCell } from '@/components/ui/table'
import { RowActions } from '@/components/ui/row-actions'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import {
  useVenueTimelineTemplates,
  useCreateTimelineTemplate,
  useUpdateTimelineTemplate,
  useDeleteTimelineTemplate,
} from '@/hooks/useTimeline'
import { timeOfDaySchema } from '@lib/validators/time-of-day.validator'
import type { VenueTimelineTemplateDto } from '@lib/dto/event-timeline.dto'
import type { VenueTimelineEntryInput } from '@lib/input/event-timeline.input'
import { TimelineLabelField, timelineLabelError } from '@/components/TimelineLabelField'

/** A row being edited. Kept as strings so a half-typed time is representable. */
interface DraftRow {
  label: string
  startsAt: string
}

/**
 * A venue's timeline templates — the shapes its days usually take.
 *
 * The list-then-dialog shape `FurnitureGroupsPanel` uses, because this is the
 * same problem: several named things, each of which opens an editor. See
 * `docs/event-timeline.md`.
 */
export function VenueTimelinesPanel({
  venueId,
  canEdit,
}: {
  venueId: string
  /** Edit adds creating, changing, deleting and choosing the default template; without it each is only read. */
  canEdit: boolean
}) {
  const templatesQuery = useVenueTimelineTemplates(venueId)
  const createTemplate = useCreateTimelineTemplate(venueId)
  const updateTemplate = useUpdateTimelineTemplate(venueId)
  const deleteTemplate = useDeleteTimelineTemplate(venueId)

  const [open, setOpen] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [name, setName] = React.useState('')
  const [rows, setRows] = React.useState<DraftRow[]>([])
  const [dragFrom, setDragFrom] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  // The template being read, for someone who can't change it.
  const [viewing, setViewing] = React.useState<VenueTimelineTemplateDto | null>(null)
  const [pendingDelete, setPendingDelete] = React.useState<{
    id: string
    name: string
    closeEditor: boolean
  } | null>(null)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)
  // "Make default" is a row action, so its refusal is said under the list.
  const [defaultError, setDefaultError] = React.useState<string | null>(null)

  const templates = templatesQuery.data?.templates ?? []
  const busy = createTemplate.isPending || updateTemplate.isPending || deleteTemplate.isPending

  function openCreate(): void {
    setEditingId(null)
    setName('')
    setRows([])
    setError(null)
    setOpen(true)
  }

  function openEdit(template: VenueTimelineTemplateDto): void {
    setEditingId(template.id)
    setName(template.name)
    setRows(template.entries.map((entry) => ({ label: entry.label, startsAt: entry.startsAt })))
    setError(null)
    setOpen(true)
  }

  function setRow(index: number, patch: Partial<DraftRow>): void {
    setError(null)
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  /**
   * Move a row, which is the only way to say a template crosses midnight.
   *
   * The order here *is* the running order — it becomes `sortOrder` — so a day
   * ending at 00:30 needs that row placed last by hand. Sorting by the clock
   * would put it first and describe a different day entirely.
   */
  function moveRow(from: number, to: number): void {
    setRows((current) => {
      if (to < 0 || to >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(from, 1)
      if (moved) next.splice(to, 0, moved)
      return next
    })
  }

  async function save(): Promise<void> {
    setError(null)
    const cleaned: VenueTimelineEntryInput[] = []
    for (const [index, row] of rows.entries()) {
      const label = row.label.trim()
      if (label === '') {
        setError(`Row ${String(index + 1)} needs a description.`)
        return
      }
      const tooLong = timelineLabelError(row.label)
      if (tooLong !== null) {
        setError(`Row ${String(index + 1)}: ${tooLong}`)
        return
      }
      const time = timeOfDaySchema.safeParse(row.startsAt)
      if (!time.success) {
        setError(`Row ${String(index + 1)} needs a time like 17:00.`)
        return
      }
      cleaned.push({ label, startsAt: time.data })
    }
    const trimmed = name.trim()
    if (trimmed === '') {
      setError('Give the template a name.')
      return
    }
    try {
      if (editingId) await updateTemplate.mutateAsync({ templateId: editingId, input: { name: trimmed, entries: cleaned } })
      else await createTemplate.mutateAsync({ name: trimmed, entries: cleaned })
      setOpen(false)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  function askDelete(id: string, templateName: string, closeEditor: boolean): void {
    setDeleteError(null)
    setPendingDelete({ id, name: templateName, closeEditor })
  }

  function makeDefault(template: VenueTimelineTemplateDto): void {
    setDefaultError(null)
    updateTemplate.mutate(
      { templateId: template.id, input: { isDefault: true } },
      {
        onError: (e) =>
          setDefaultError(`“${template.name}” could not be made the default. ${requestErrorMessage(e)}`),
      },
    )
  }

  function confirmDelete(): void {
    if (!pendingDelete) return
    const { id, closeEditor } = pendingDelete
    deleteTemplate.mutate(
      { templateId: id },
      {
        onSuccess: () => {
          if (closeEditor) setOpen(false)
          setPendingDelete(null)
          setDeleteError(null)
        },
        onError: (e) => setDeleteError(requestErrorMessage(e)),
      },
    )
  }

  function renderList() {
    if (templatesQuery.isPending) return <LoadingState label="Loading timelines…" />
    if (templatesQuery.isError) {
      return (
        <ErrorState
          message={requestErrorMessage(templatesQuery.error)}
          onRetry={() => void templatesQuery.refetch()}
        />
      )
    }
    if (templates.length === 0) {
      if (!canEdit) {
        return (
          <EmptyState
            icon={Clock}
            title="No timeline templates yet"
            description="This venue hasn’t set out the shape of a usual day yet."
          />
        )
      }
      return (
        <EmptyState
          icon={Clock}
          title="No timeline templates yet"
          description="Set out the shape of a usual day — a Saturday wedding, a weekday corporate booking — and new events at this venue start from it."
          action={
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New template
            </Button>
          }
        />
      )
    }
    return (
      <Table
        heading={`${String(templates.length)} template${templates.length === 1 ? '' : 's'}`}
        actions={
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> New template
            </Button>
          ) : undefined
        }
      >
        <TableHeader>
          <TableHead>Template</TableHead>
          <TableHead>Entries</TableHead>
          <TableHead>Starts</TableHead>
          {canEdit ? <TableHead /> : null}
        </TableHeader>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.id} onClick={() => (canEdit ? openEdit(template) : setViewing(template))}>
              <TableCell>
                <span className="flex items-center gap-2">
                  <span className="font-medium">{template.name}</span>
                  {template.isDefault ? <Badge>Default</Badge> : null}
                </span>
              </TableCell>
              <TableCell>{template.entries.length}</TableCell>
              <TableCell className="text-ink-muted">{template.entries[0]?.startsAt ?? '—'}</TableCell>
              {canEdit ? (
                <TableActionsCell>
                  <RowActions
                    label={template.name}
                    actions={[
                      {
                        label: 'Edit',
                        icon: Pencil,
                        priority: true,
                        onSelect: () => openEdit(template),
                      },
                      {
                        label: 'Make default',
                        icon: Star,
                        onSelect: () => makeDefault(template),
                        disabled: template.isDefault,
                        disabledReason: 'This is already the template new events start from.',
                      },
                      {
                        label: 'Delete',
                        icon: Trash2,
                        destructive: true,
                        onSelect: () => askDelete(template.id, template.name, false),
                      },
                    ]}
                  />
                </TableActionsCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <div className="space-y-6">
      {renderList()}
      {defaultError ? <FormError>{defaultError}</FormError> : null}

      <Dialog
        open={viewing !== null}
        onOpenChange={(next) => {
          if (!next) setViewing(null)
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.name ?? 'Timeline template'}</DialogTitle>
            <DialogDescription>Each moment of the day, in running order.</DialogDescription>
          </DialogHeader>
          {viewing === null || viewing.entries.length === 0 ? (
            <p className="text-sm text-ink-muted">Nothing in this template yet.</p>
          ) : (
            <ol className="space-y-2">
              {viewing.entries.map((entry, index) => (
                <li key={index} className="flex items-baseline gap-3 text-sm">
                  <span className="w-14 shrink-0 font-mono tabular-nums text-ink-muted">{entry.startsAt}</span>
                  <span className="min-w-0 whitespace-pre-line break-words">{entry.label}</span>
                </li>
              ))}
            </ol>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setViewing(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit timeline template' : 'New timeline template'}</DialogTitle>
            <DialogDescription>
              A time and a description for each moment. The order you put them in is the running order — which is what
              lets a day end after midnight.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <Field label="Name" htmlFor="tt-name">
              <Input
                id="tt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Saturday wedding"
                autoFocus
              />
            </Field>

            <Field label="The day">
              <div className="space-y-2">
                {rows.length === 0 ? (
                  <p className="text-sm text-ink-muted">Nothing yet. Add the first moment below.</p>
                ) : null}
                {rows.map((row, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2"
                    draggable
                    onDragStart={() => setDragFrom(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragFrom !== null) moveRow(dragFrom, index)
                      setDragFrom(null)
                    }}
                  >
                    {/* Level with the first line: a description can wrap onto more. */}
                    <span className="mt-[11px] cursor-grab text-ink-muted" aria-hidden="true">
                      <GripVertical className="h-4 w-4" />
                    </span>
                    <Input
                      type="time"
                      value={row.startsAt}
                      onChange={(e) => setRow(index, { startsAt: e.target.value })}
                      className="w-32"
                      aria-label={`Time for row ${String(index + 1)}`}
                    />
                    <TimelineLabelField
                      value={row.label}
                      onChange={(label) => setRow(index, { label })}
                      placeholder="e.g. Ceremony"
                      aria-label={`Description for row ${String(index + 1)}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove row ${String(index + 1)}`}
                      onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRows((current) => [...current, { label: '', startsAt: '12:00' }])}
                >
                  <Plus className="h-4 w-4" /> Add row
                </Button>
              </div>
            </Field>

            {error ? <FormError>{error}</FormError> : null}
          </div>

          <DialogFooter>
            {editingId ? (
              <Button
                type="button"
                variant="outline"
                className="mr-auto text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
                disabled={deleteTemplate.isPending}
                onClick={() => {
                  const saved = templates.find((template) => template.id === editingId)
                  askDelete(editingId, saved?.name ?? (name.trim() === '' ? 'this template' : name.trim()), true)
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={busy || name.trim() === '' || rows.some((row) => timelineLabelError(row.label) !== null)}
            >
              {busy ? 'Saving…' : editingId ? 'Save changes' : 'Create template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (next || deleteTemplate.isPending) return
          setPendingDelete(null)
          setDeleteError(null)
        }}
        title={pendingDelete ? `Delete “${pendingDelete.name}”?` : 'Delete template?'}
        description="New events will no longer start from it. Events that already have a timeline keep theirs. If this is the default, the venue has no default until another template is chosen."
        confirmLabel="Delete template"
        destructive
        busy={deleteTemplate.isPending}
        error={deleteError}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
